import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Briefcase, UserCheck, Building2, Rocket, CheckCircle2 } from 'lucide-react';
import { Testimonials } from '../components/Testimonials';
const services = [{
  icon: Briefcase,
  title: 'C-Suite Search',
  subtitle: 'CEO, COO, CFO, CTO & CIO',
  description: "Placing proven leaders in your organization's most critical roles. We understand the impact of C-level appointments and employ a rigorous process to identify executives capable of driving meaningful organizational progress.",
  capabilities: ['Chief Executive Officer searches', 'Chief Operating Officer placement', 'Chief Financial Officer recruitment', 'Chief Technology & Information Officer searches', 'Succession planning for outgoing executives']
}, {
  icon: UserCheck,
  title: 'VP-Level Recruitment',
  subtitle: 'Vice Presidents Across All Functions',
  description: 'Recruiting experienced Vice Presidents who effectively translate executive vision into operational results. We focus on leaders capable of driving performance and developing strong teams.',
  capabilities: ['VP of Engineering & Product', 'VP of Sales & Business Development', 'VP of Marketing & Growth', 'VP of People & Operations', 'VP of Finance & Strategy']
}, {
  icon: Building2,
  title: 'Board Advisory',
  subtitle: 'Independent Directors & Advisors',
  description: 'Connecting organizations with independent directors who provide strategic insight, governance expertise, and valuable perspectives. A well-composed board is essential for sustainable growth and strong governance.',
  capabilities: ['Independent board director searches', 'Advisory board composition', 'Board diversity initiatives', 'Governance expertise matching', 'Industry-specific board talent']
}, {
  icon: Rocket,
  title: 'Startup Leadership',
  subtitle: 'Seed Stage Through Series A',
  description: 'Dedicated search services for early-stage companies making foundational leadership hires. We understand the unique requirements of seed and Series A organizations and help founders build their initial executive teams.',
  capabilities: ['First VP-level hires for founding teams', 'Technical co-founder matching', 'Go-to-market leadership placement', 'Scaling-stage executive recruitment', 'Founder-executive culture fit assessment']
}];
export function ServicesPage({
  onBack
}: {
  onBack: () => void;
}) {
  return <div className="min-h-screen bg-white pt-28 pb-20" data-id="element-217">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-218">
        {/* Back link */}
        <motion.button initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} onClick={onBack} className="flex items-center text-primary hover:text-primary-dark transition-colors mb-12 group" data-id="element-219">
          <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-220" />
          <span className="text-sm uppercase tracking-widest font-medium" data-id="element-221">
            Back to Home
          </span>
        </motion.button>

        {/* Page heading */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mb-20" data-id="element-222">
          <div className="w-16 h-1 bg-primary mb-8" data-id="element-223"></div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-4" data-id="element-224">
            Our Expertise
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl" data-id="element-225">
            We specialize in placing senior leaders in roles where the right
            hire makes a measurable difference. Every search is conducted with
            the rigor, discretion, and focus that critical appointments demand.
          </p>
        </motion.div>

        {/* Service sections */}
        <div className="space-y-24" data-id="element-226">
          {services.map((service, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15 + index * 0.1
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start" data-id="element-227">
              {/* Icon + Title */}
              <div className="lg:col-span-4" data-id="element-228">
                <div className="flex items-center mb-4" data-id="element-229">
                  <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mr-4" data-id="element-230">
                    <service.icon className="w-7 h-7 text-primary" strokeWidth={1.5} data-id="element-231" />
                  </div>
                  <div data-id="element-232">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-233">
                      {service.title}
                    </h2>
                    <p className="text-sm text-primary font-medium uppercase tracking-wider" data-id="element-234">
                      {service.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description + Capabilities */}
              <div className="lg:col-span-8" data-id="element-235">
                <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-236">
                  {service.description}
                </p>
                <ul className="space-y-3" data-id="element-237">
                  {service.capabilities.map((cap, i) => <li key={i} className="flex items-center text-gray-600" data-id="element-238">
                      <CheckCircle2 className="w-5 h-5 text-primary mr-3 flex-shrink-0" strokeWidth={1.5} data-id="element-239" />
                      {cap}
                    </li>)}
                </ul>
                {index < services.length - 1 && <div className="mt-12 border-b border-gray-100" data-id="element-240"></div>}
              </div>
            </motion.div>)}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="mt-24 bg-gray-50 p-10 md:p-14 text-center" data-id="element-241">
          <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-242">
            Start a Conversation
          </h3>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-243">
            Every successful search begins with a conversation. Share your
            leadership requirements with us, and we can discuss how our approach
            aligns with your needs.
          </p>
          <button onClick={() => {
          onBack();
        }} className="inline-flex items-center px-8 py-3 bg-primary text-white font-medium tracking-wide hover:bg-primary-dark transition-colors duration-200" data-id="element-244">
            Get in Touch
          </button>
        </motion.div>
      </div>

      {/* Testimonials section - full width */}
      <Testimonials data-id="element-245" />
    </div>;
}